import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { UserCheck, Shield, Eye, Lock, ChevronDown, MessageSquare, ListChecks, Brain, FileText, Code2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Inline SVG flag — Windows doesn't render regional indicator emojis (🇫🇷)
const FrFlag = ({ className = 'w-4 h-4' }: { className?: string }) => (
    <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg" aria-label="France">
        <rect width="1" height="2" fill="#002395" />
        <rect x="1" width="1" height="2" fill="#fff" />
        <rect x="2" width="1" height="2" fill="#ED2939" />
    </svg>
);

const trustPoints = [
    { icon: UserCheck, titleKey: 'trust_point1_title', descKey: 'trust_point1_desc' },
    { icon: Shield,    titleKey: 'trust_point2_title', descKey: 'trust_point2_desc' },
    { icon: Eye,       titleKey: 'trust_point3_title', descKey: 'trust_point3_desc' },
    { icon: Lock,      titleKey: 'trust_point4_title', descKey: 'trust_point4_desc' },
];

const skills = [
    { icon: MessageSquare, key: 'communication', color: 'var(--accent)' },
    { icon: ListChecks,    key: 'scheduling',    color: '#34d399' },
    { icon: Brain,         key: 'research',      color: '#e879f9' },
    { icon: FileText,      key: 'docs',          color: '#fbbf24' },
    { icon: Code2,         key: 'code',          color: '#22d3ee' },
    { icon: AlertTriangle, key: 'escalation',    color: '#f43f5e' },
];

/**
 * WaitlistDeepDive
 * 
 * Extracted from WaitlistPage.tsx — the collapsible "Go deeper" section containing:
 *   - Capabilities grid
 *   - Trust architecture points
 *   - IP numbers (patents, claims)
 *   - Compliance badges
 * 
 * Self-contained with its own state for the accordion toggle.
 */
const WaitlistDeepDive: React.FC = () => {
    const { t } = useTranslation('marketing');
    const [deepDiveOpen, setDeepDiveOpen] = useState(false);

    return (
        <section className="relative py-10 md:py-14" id="deep-dive">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
            <div className="container-hig">
                <div className="max-w-3xl mx-auto">
                    <button
                        onClick={() => setDeepDiveOpen(!deepDiveOpen)}
                        className="w-full flex items-center justify-center gap-3 p-5 rounded-xl border border-hig-border bg-hig-bg-tertiary/30 hover:bg-hig-bg-tertiary/50 transition-all duration-300 group"
                    >
                        <span className="text-sm font-medium text-hig-text-secondary group-hover:text-hig-text-secondary transition-colors">
                            {t('waitlist.deep_dive_button', 'Go deeper. capabilities, trust architecture, and IP position')}
                        </span>
                        <ChevronDown
                            className={`w-4 h-4 text-hig-text-tertiary transition-transform duration-300 ${deepDiveOpen ? 'rotate-180' : ''}`}
                        />
                    </button>

                    <AnimatePresence>
                        {deepDiveOpen && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.4, ease: 'easeInOut' }}
                                className="overflow-hidden"
                            >
                                <div className="pt-12 space-y-20">

                {/* ── CAPABILITIES ── */}
                <div>
                    <div className="max-w-2xl mx-auto">
                            <motion.h2
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-80px' }}
                                transition={{ duration: 0.6 }}
                                className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-3 tracking-tight"
                            >
                                {t('waitlist.skills_title')}
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-80px' }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-sm text-hig-text-tertiary mb-10"
                            >
                                {t('waitlist.skills_subtitle')}
                            </motion.p>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {skills.map((skill, i) => {
                                    const IconComp = skill.icon;
                                    return (
                                        <motion.div
                                            key={skill.key}
                                            initial={{ opacity: 0, y: 12 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true, margin: '-40px' }}
                                            transition={{ duration: 0.45, delay: i * 0.05 }}
                                            className="group relative flex flex-col gap-2.5 p-4 rounded-xl border border-hig-border bg-hig-bg-tertiary/30 hover:bg-hig-bg-tertiary/40 hover:border-hig-border/80 transition-all duration-300"
                                        >
                                            <div
                                                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                                style={{ background: `radial-gradient(circle at 30% 30%, ${skill.color}10, transparent 70%)` }}
                                            />
                                            <div
                                                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                                                style={{ background: `${skill.color}18`, border: `1px solid ${skill.color}35` }}
                                            >
                                                <IconComp className="w-4 h-4" style={{ color: skill.color }} />
                                            </div>
                                            <div>
                                                <p className="text-[12px] font-semibold text-hig-text-secondary leading-tight mb-1">
                                                    {t(`waitlist.skill_${skill.key}`)}
                                                </p>
                                                <p className="text-[10px] text-hig-text-tertiary leading-relaxed">
                                                    {t(`waitlist.skill_${skill.key}_desc`)}
                                                </p>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                className="text-[12px] text-hig-text-tertiary mt-10 font-medium italic"
                            >
                                {t('waitlist.skills_bottom_line')}
                            </motion.p>
                    </div>
                </div>

                {/* ── TRUST ── */}
                <div>
                    <div className="max-w-2xl mx-auto">
                            <motion.h2
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-80px' }}
                                transition={{ duration: 0.6 }}
                                className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-4 tracking-tight"
                            >
                                {t('waitlist.trust_title')}
                            </motion.h2>
                            <p className="text-sm text-hig-text-tertiary mb-10">
                                {t('waitlist.trust_intro')}
                            </p>

                            <div className="space-y-4">
                                {trustPoints.map((pt, i) => {
                                    const IconComp = pt.icon;
                                    return (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, x: -12 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true, margin: '-40px' }}
                                            transition={{ duration: 0.5, delay: i * 0.08 }}
                                            className="flex items-start gap-4 p-4 rounded-xl border border-hig-border bg-hig-bg-tertiary/30"
                                        >
                                            <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-hig-accent/10 border border-hig-accent/20">
                                                <IconComp className="w-4 h-4 text-hig-accent" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-semibold text-hig-text-primary mb-1">
                                                    {t(`waitlist.${pt.titleKey}`)}
                                                </h3>
                                                <p className="text-[11px] text-hig-text-tertiary leading-relaxed">
                                                    {t(`waitlist.${pt.descKey}`)}
                                                </p>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            <p className="text-[12px] text-hig-text-tertiary mt-10 font-medium italic">
                                {t('waitlist.trust_closing')}
                            </p>
                    </div>
                </div>

                {/* ── IP NUMBERS ── */}
                <div>
                    <div className="max-w-3xl mx-auto">
                            <motion.h2
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-80px' }}
                                transition={{ duration: 0.6 }}
                                className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-4 tracking-tight text-center"
                            >
                                {t('waitlist.numbers_title')}
                            </motion.h2>
                            <p className="text-sm text-hig-text-tertiary text-center mb-8 max-w-lg mx-auto">
                                {t('waitlist.numbers_subtitle')}
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                    transition={{ duration: 0.5 }}
                                    className="relative text-center p-8 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04]"
                                >
                                    <span className="text-5xl md:text-6xl font-bold text-amber-400 block mb-3 tracking-tight">20+</span>
                                    <span className="text-sm font-semibold text-hig-text-secondary block mb-1">{t('waitlist.numbers_patents_label')}</span>
                                    <span className="text-[10px] text-hig-text-tertiary">{t('waitlist.numbers_patents_sub')}</span>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                    transition={{ duration: 0.5, delay: 0.1 }}
                                    className="relative text-center p-8 rounded-2xl border border-hig-accent/20 bg-hig-accent/[0.04]"
                                >
                                    <span className="text-5xl md:text-6xl font-bold text-hig-accent block mb-3 tracking-tight">175+</span>
                                    <span className="text-sm font-semibold text-hig-text-secondary block mb-1">{t('waitlist.numbers_claims_label')}</span>
                                    <span className="text-[10px] text-hig-text-tertiary">{t('waitlist.numbers_claims_sub')}</span>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                    transition={{ duration: 0.5, delay: 0.2 }}
                                    className="relative text-center p-8 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.04]"
                                >
                                    <span className="text-5xl md:text-6xl font-bold text-emerald-400 block mb-3 tracking-tight">1</span>
                                    <span className="text-sm font-semibold text-hig-text-secondary block mb-1">{t('waitlist.numbers_owner_label')}</span>
                                    <span className="text-[10px] text-hig-text-tertiary">{t('waitlist.numbers_owner_sub')}</span>
                                </motion.div>
                            </div>

                            <p className="text-[11px] text-hig-text-tertiary text-center mt-10 tracking-wide">
                                {t('waitlist.numbers_footer')}
                            </p>

                            {/* ── COMPLIANCE BADGES ── */}
                            <div className="mt-14 pt-10 border-t border-hig-border">
                                <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-center mb-8" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>
                                    {t('waitlist.compliance_section_eyebrow', 'ORIGIN & COMPLIANCE')}
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[
                                        { icon: <FrFlag className="w-6 h-6" />, label: t('waitlist.compliance_badge_france_label'), desc: t('waitlist.compliance_badge_france_desc'), borderColor: '#3b82f6', textColor: '#2563eb', bg: 'rgba(59,130,246,0.07)' },
                                        { icon: '⚖️', label: t('waitlist.compliance_badge_aiact_label'), desc: t('waitlist.compliance_badge_aiact_desc'), borderColor: '#d97706', textColor: '#b45309', bg: 'rgba(217,119,6,0.07)' },
                                        { icon: '🔐', label: t('waitlist.compliance_badge_gdpr_label'), desc: t('waitlist.compliance_badge_gdpr_desc'), borderColor: '#059669', textColor: '#047857', bg: 'rgba(5,150,105,0.07)' },
                                        { icon: '📋', label: t('waitlist.compliance_badge_inpi_label'), desc: t('waitlist.compliance_badge_inpi_desc'), borderColor: '#7c3aed', textColor: '#6d28d9', bg: 'rgba(124,58,237,0.07)' },
                                    ].map(({ icon, label, desc, borderColor, textColor, bg }) => (
                                        <motion.div
                                            key={label}
                                            initial={{ opacity: 0, y: 12 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true, margin: '-40px' }}
                                            transition={{ duration: 0.4 }}
                                            className="flex flex-col items-center text-center p-5 rounded-xl"
                                            style={{ border: `1px solid ${borderColor}`, background: bg }}
                                        >
                                            <span className="text-2xl mb-3">{icon}</span>
                                            <span className="text-[11px] font-bold tracking-wide mb-1.5" style={{ color: textColor }}>{label}</span>
                                            <span className="text-[9px] leading-relaxed" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>{desc}</span>
                                        </motion.div>
                                    ))}
                                </div>
                                <p className="text-[10px] text-center mt-6 tracking-wide" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>
                                    {t('waitlist.compliance_footer_note')}
                                </p>
                            </div>

                    </div>
                </div>

                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </section>
    );
};

export default WaitlistDeepDive;
